import axios from 'axios';
import { Proprietaire } from '../types';
import { API_BASE_URL } from '../config';

// Configuration d'axios avec intercepteur pour le token
const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ProprietaireFormData {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  pays?: string;
  username?: string; // Optionnel - sera généré automatiquement si non fourni
}

export interface ProprietaireResponse {
  id: number;
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  pays?: string;
  user: {
    id: number;
    username: string;
    email: string;
    is_active: boolean;
    date_joined: string;
    veterinaire_selectionne?: number;
    veterinaire_selectionne_nom?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface PaginatedProprietairesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProprietaireResponse[];
}

class ProprietaireService {
  
  // Récupérer la liste des propriétaires avec pagination
  async getProprietaires(page: number = 1, search?: string): Promise<PaginatedProprietairesResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      if (search) {
        params.append('search', search);
      }
      
      const response = await api.get(`/proprietaires/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des propriétaires:', error);
      throw error;
    }
  }

  // Récupérer un propriétaire par ID
  async getProprietaireById(id: number): Promise<ProprietaireResponse> {
    try {
      const response = await api.get(`/proprietaires/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du propriétaire ${id}:`, error);
      throw error;
    }
  }

  // Créer un nouveau propriétaire avec utilisateur associé
  async createProprietaire(data: ProprietaireFormData): Promise<ProprietaireResponse> {
    try {
      // Si pas de username fourni, on le génère automatiquement
      const proprietaireData = {
        ...data,
        username: data.username || `${data.prenom.toLowerCase()}.${data.nom.toLowerCase()}`,
        // Le mot de passe par défaut sera géré côté backend
      };

      const response = await api.post('/proprietaires/', proprietaireData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du propriétaire:', error);
      throw error;
    }
  }

  // Mettre à jour un propriétaire
  async updateProprietaire(id: number, data: Partial<ProprietaireFormData>): Promise<ProprietaireResponse> {
    try {
      const response = await api.patch(`/proprietaires/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du propriétaire ${id}:`, error);
      throw error;
    }
  }

  // Désactiver un propriétaire (soft delete)
  async deactivateProprietaire(id: number): Promise<void> {
    try {
      // On désactive l'utilisateur associé plutôt que de supprimer
      await api.patch(`/proprietaires/${id}/deactivate/`);
    } catch (error) {
      console.error(`Erreur lors de la désactivation du propriétaire ${id}:`, error);
      throw error;
    }
  }

  // Réactiver un propriétaire
  async reactivateProprietaire(id: number): Promise<void> {
    try {
      await api.patch(`/proprietaires/${id}/reactivate/`);
    } catch (error) {
      console.error(`Erreur lors de la réactivation du propriétaire ${id}:`, error);
      throw error;
    }
  }

  // Rechercher des propriétaires
  async searchProprietaires(query: string): Promise<ProprietaireResponse[]> {
    try {
      const response = await api.get(`/proprietaires/search/?q=${encodeURIComponent(query)}`);
      return response.data.results || response.data;
    } catch (error) {
      console.error('Erreur lors de la recherche de propriétaires:', error);
      throw error;
    }
  }

  // Réinitialiser le mot de passe d'un propriétaire
  async resetPassword(id: number): Promise<{ new_password: string }> {
    try {
      const response = await api.post(`/proprietaires/${id}/reset-password/`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la réinitialisation du mot de passe du propriétaire ${id}:`, error);
      throw error;
    }
  }
}

export default new ProprietaireService();
